import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSesja } from '@/hooks/useSesja';
import { supabase } from '@/integrations/supabase/client';
import { respondProfileInfo as respondProfileInfoRpc } from '@/lib/consent/profileInfo';
import { respondTenantLink } from '@/lib/respondTenantLink';
import { toast } from 'sonner';
import i18n from 'i18next';
import type { Tables } from '@/integrations/supabase/types';

export type Notification = Tables<'notifications'>;

export const NOTIFICATIONS_QUERY_KEY = ['notifications'] as const;

/** Message pings live on the Messages icon (unread badge), not in the bell —
 *  the market-standard split (see docs/MessagingProjectPlan.md EPIC 3). */
export const MESSAGE_NOTIFICATION_TYPE = 'message_received';

/** Number of notifications that have not yet been seen. */
export function countUnseen(list: Notification[]): number {
  return list.reduce((n, item) => (item.status === 'unseen' ? n + 1 : n), 0);
}

/** Notifications shown in the bell / notifications page — everything except
 *  message pings, which are surfaced as a badge on the Messages icon. */
export function bellNotifications(list: Notification[]): Notification[] {
  return list.filter((item) => item.type !== MESSAGE_NOTIFICATION_TYPE);
}

/** Unread message pings — drives the unread-count badge on the Messages icon. */
export function countUnseenMessages(list: Notification[]): number {
  return list.reduce(
    (n, item) =>
      item.type === MESSAGE_NOTIFICATION_TYPE && item.status === 'unseen' ? n + 1 : n,
    0,
  );
}

async function resolveUserId(): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return null;
  const { data } = await supabase
    .from('users')
    .select('id')
    .eq('auth_id', user.id)
    .maybeSingle();
  return data?.id ?? null;
}

/**
 * In-app notifications for the current user. Fetches the user's notifications,
 * exposes the unseen count, lets callers mark items seen (single or all), and
 * subscribes to realtime inserts/updates so the bell stays live.
 *
 * Seen ≠ acted upon: marking seen only flips the status badge; any inline
 * action (e.g. Accept/Reject on an invite) is handled separately by the caller.
 */
export function useNotifications() {
  const queryClient = useQueryClient();
  const [userId, setUserId] = useState<string | null>(null);

  // The list belongs to the LOGGED-IN account, so the key carries it (Maciek 20.10): the header's
  // bell is mounted before login, fetched as nobody (empty list), and with a fixed key nothing
  // refetched after login until the 60 s safety net — the bell and the page stayed empty for up
  // to a minute. Now login → new key → immediate fetch; logout → no query, no stale list.
  const sesja = useSesja();
  const authId = sesja?.user.id ?? null;
  const query = useQuery({
    queryKey: [...NOTIFICATIONS_QUERY_KEY, authId ?? 'gosc'],
    enabled: !!authId,
    queryFn: async (): Promise<Notification[]> => {
      const uid = await resolveUserId();
      setUserId(uid);
      if (!uid) return [];
      const { data } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', uid)
        .order('created_at', { ascending: false });
      return data ?? [];
    },
    // Realtime (below) is the primary live channel; these are a safety net so
    // the bell still updates within a minute if the websocket drops.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const allNotifications = query.data ?? [];
  // The bell shows everything except message pings; messages get their own
  // unread badge on the Messages icon (market-standard split).
  const notifications = bellNotifications(allNotifications);
  const unseenCount = countUnseen(notifications);
  const messageUnseenCount = countUnseenMessages(allNotifications);

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY }),
    [queryClient],
  );

  const markSeen = useCallback(
    async (id: string) => {
      await supabase
        .from('notifications')
        .update({ status: 'seen', seen_at: new Date().toISOString() })
        .eq('id', id)
        .eq('status', 'unseen');
      await invalidate();
    },
    [invalidate],
  );

  // Opening the bell marks everything *except* message pings as seen — those
  // clear only when the user actually opens Messages (markMessagesSeen).
  // Resolves the user fresh each call: this hook is mounted in several places
  // sharing one React Query, so only one instance's queryFn (and its setUserId)
  // ever runs — relying on `userId` state here would no-op in the others.
  const markAllSeen = useCallback(async () => {
    const uid = await resolveUserId();
    if (!uid) return;
    await supabase
      .from('notifications')
      .update({ status: 'seen', seen_at: new Date().toISOString() })
      .eq('user_id', uid)
      .eq('status', 'unseen')
      .neq('type', MESSAGE_NOTIFICATION_TYPE);
    await invalidate();
  }, [invalidate]);

  // Clears the Messages unread badge — called when the user opens /messages.
  const markMessagesSeen = useCallback(async () => {
    const uid = await resolveUserId();
    if (!uid) return;
    await supabase
      .from('notifications')
      .update({ status: 'seen', seen_at: new Date().toISOString() })
      .eq('user_id', uid)
      .eq('type', MESSAGE_NOTIFICATION_TYPE)
      .eq('status', 'unseen');
    await invalidate();
  }, [invalidate]);

  // Invites the user has accepted/rejected this session, so the inline buttons
  // on the matching invite_received notification disappear immediately.
  const [respondedLinkIds, setRespondedLinkIds] = useState<Set<string>>(new Set());

  // Accept/reject an invite straight from a notification (Path C). Same call and the same feedback as
  // the profile's „Współnajemcy” box (`respondTenantLink`). Until 19.10 the reply was ignored: the
  // buttons vanished and nothing was said even when the server refused (expired invite, already
  // linked) — audyt 19.10. Now the invite counts as answered only on success, and both lists are
  // refreshed either way, so an archived invite disappears instead of failing again.
  const respondInvite = useCallback(
    async (notificationId: string, linkId: string, action: 'accept' | 'reject'): Promise<boolean> => {
      const ok = await respondTenantLink(linkId, action);
      if (ok) {
        setRespondedLinkIds((prev) => new Set(prev).add(linkId));
        await supabase
          .from('notifications')
          .update({ status: 'seen', seen_at: new Date().toISOString() })
          .eq('id', notificationId)
          .eq('status', 'unseen');
        toast.success(i18n.t(action === 'accept' ? 'co_tenants.invite_accepted_toast' : 'co_tenants.invite_rejected_toast'));
      } else {
        toast.error(i18n.t('common.error'));
      }
      await invalidate();
      queryClient.invalidateQueries({ queryKey: ['tenant_links'] });
      return ok;
    },
    [invalidate, queryClient],
  );

  // Consent requests the user has approved/rejected this session, so the inline buttons on the
  // matching profile_info_requested notification disappear immediately. Used when a CO-TENANT (who
  // isn't in the landlord thread) responds to a "Praca i referencje" request straight from the bell.
  const [respondedRequestIds, setRespondedRequestIds] = useState<Set<string>>(new Set());

  const respondProfileInfo = useCallback(
    async (notificationId: string, requestId: string, approve: boolean) => {
      await respondProfileInfoRpc(requestId, approve);
      setRespondedRequestIds((prev) => new Set(prev).add(requestId));
      await supabase
        .from('notifications')
        .update({ status: 'seen', seen_at: new Date().toISOString() })
        .eq('id', notificationId)
        .eq('status', 'unseen');
      await invalidate();
    },
    [invalidate],
  );

  // Keep the bell live: any insert/update to this user's notifications refetches.
  // Unique channel name per subscription — the bell is mounted more than once
  // (desktop + mobile nav), and a shared name makes the 2nd reuse the 1st's
  // already-subscribed channel, which throws ("cannot add callbacks after subscribe").
  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      .channel(`notifications:${userId}:${crypto.randomUUID()}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'notifications', filter: `user_id=eq.${userId}` },
        () => {
          invalidate();
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, invalidate]);

  return {
    notifications,
    unseenCount,
    messageUnseenCount,
    isLoading: query.isLoading,
    markSeen,
    markAllSeen,
    markMessagesSeen,
    respondInvite,
    respondedLinkIds,
    respondProfileInfo,
    respondedRequestIds,
  };
}
